import { capitalisedTermForCompany } from "../../copy/sharedCopy";
import {
    ApiDriverDetailsResponse,
    DriverListOrderByOptions,
} from "../../models/api/admin/driver";
import { driverPointsColor } from "../../utils/driverPointsColour";
import { formatDateToString } from "../../utils/formatDateToString";
import { TableContent, TableHeader } from "../ReusableComponents/Table";
import UserIcon from "../UserIcon";
import {
    DriverVehicleTableAllowedIds,
    driverVehicleTableContent,
    driverVehicleTableHeaders,
} from "../ReusableComponents/SharedTableContent/driverVehicleTableContent";
import {
    DriverAddressInformation,
    DriverVehicleInformation,
} from "../../models/api/admin/driverDetails";
import {
    DriverAddressTableAllowedIds,
    driverAddressTableContent,
    driverAddressTableHeaders,
} from "../ReusableComponents/SharedTableContent/driverAddressTableContent";
import { CompanyRiskProfile } from "../../models/app/admin/company";
import { AllowedColumnIdsRecord } from "../../models/app/admin/driver";
import HighlightedDate from "../ReusableComponents/HighlightedDate";
import { useDueToExpireSettings } from "../../hooks/useDueToExpireSettings";

export type DriverListContent = {
    content: string | JSX.Element;
    id: string;
};

enum MenuGroups {
    PersonalInformation = "Personal information",
    Declaration = "Declaration",
    Employment = "Employment",
    DrivingLicence = "Driving licence",
    VehicleInformation = "Vehicle information",
    Address = "Address",
    LinkedDriver = "Linked driver",
    Comments = "Comments",
}

export const driverListMenuGroups: MenuGroups[] = (
    Object.keys(MenuGroups) as Array<keyof typeof MenuGroups>
).map((key) => MenuGroups[key]);

export const driverTableAllowedIdsEn = {
    driverId: "driverId",
    name: "name",
    email: "emailAddress",
    dateOfBirth: "dateOfBirth",
    deletedAt: "deletedAt",
    declarationStatus: "declarationStatus",
    declarationExpiry: "declarationExpires",
    declarationLastSent: "declarationLastSent",
    company: "client",
    department: "department",
    jobRole: "jobRole",
    employeeNumber: "employeeNumber",
    externalReference: "externalReference",
    points: "points",
    ageRange: "ageRange",
    endorsementCodes: "endorsementCodes",
    licenceType: "licenceType",
    licenceStatus: "licenceStatus",
    driverRestrictions: "driverRestrictions",
    licenceCategory: "licenceCategories",
    categoryExpiryDates: "categoryExpiryDates",
    expiringCategories: "expiringCategories",
    expiredCategories: "expiredCategories",
    licenceOrigin: "licenceOrigin",
    licenceExpires: "licenceExpires",
    yearsHeld: "yearsHeldPhotocard",
    licenceNumber: "licenceNumber",
    licenceLastCheckedAtDate: "lastChecked",
    previousLicenceChecks: "previousChecks",
    licenceNextCheckDate: "nextCheck",
    linkedDriverStatus: "linkedDriverStatus",
    mainDriverName: "mainDriverName",
    comments: "comments",
    cpcLgvExpiryDate: "cpcLgvExpiryDate",
    cpcPcvExpiryDate: "cpcPcvExpiryDate",
    tachoCardExpiryDate: "tachoCardExpiryDate",
    GreyFleetInviteEmailLastSent: "GreyFleetInviteEmailLastSent",
    CpcExpiryEmailLastSent: "CpcExpiryEmailLastSent",
    AddressNonMatchEmailLastSent: "AddressNonMatchEmailLastSent",
    TachoCardExpiryEmailLastSent: "TachoCardExpiryEmailLastSent",
    photoCardExpiryEmailLastSent: "photoCardExpiryEmailLastSent",
    entitlementExpiryEmailLastSent: "entitlementExpiryEmailLastSent",
} as const satisfies AllowedColumnIdsRecord;

export type DriverTableAllowedIds =
    | (typeof driverTableAllowedIdsEn)[keyof typeof driverTableAllowedIdsEn]
    | DriverVehicleTableAllowedIds
    | DriverAddressTableAllowedIds;

export const initTableHeaders: TableHeader<DriverTableAllowedIds>[] = [
    {
        title: "Driver Id",
        id: "driverId",
        menuGroup: MenuGroups.PersonalInformation,
        hide: true,
    },
    {
        title: "Name",
        id: "name",
        orderBy: DriverListOrderByOptions.LastName,
        menuGroup: MenuGroups.PersonalInformation,
    },
    {
        title: "Email address",
        id: "emailAddress",
        hide: true,
        orderBy: DriverListOrderByOptions.EmailAddress,
        menuGroup: MenuGroups.PersonalInformation,
    },
    {
        title: "Date of birth",
        id: "dateOfBirth",
        hide: true,
        orderBy: DriverListOrderByOptions.DateOfBirth,
        menuGroup: MenuGroups.PersonalInformation,
    },
    {
        title: "Deleted at",
        id: "deletedAt",
        hide: true,
        orderBy: "DeletedAt",
        menuGroup: MenuGroups.PersonalInformation,
    },
    {
        title: "Declaration status",
        id: "declarationStatus",
        hide: true,
        menuGroup: MenuGroups.Declaration,
    },
    {
        title: "Declaration expires",
        id: "declarationExpires",
        hide: true,
        menuGroup: MenuGroups.Declaration,
    },
    {
        title: "Declaration Last Sent",
        id: "declarationLastSent",
        hide: true,
        menuGroup: MenuGroups.Declaration,
    },
    {
        title: capitalisedTermForCompany,
        id: "client",
        orderBy: DriverListOrderByOptions.Company,
        menuGroup: MenuGroups.Employment,
    },
    {
        title: "Department",
        id: "department",
        orderBy: DriverListOrderByOptions.Department,
        menuGroup: MenuGroups.Employment,
    },
    {
        title: "Job role",
        id: "jobRole",
        hide: true,
        menuGroup: MenuGroups.Employment,
    },
    {
        title: "Employee number",
        id: "employeeNumber",
        hide: true,
        orderBy: DriverListOrderByOptions.EmployeeId,
        menuGroup: MenuGroups.Employment,
    },
    {
        title: "External reference",
        id: "externalReference",
        hide: true,
        orderBy: DriverListOrderByOptions.ExternalReference,
        menuGroup: MenuGroups.Employment,
    },
    {
        title: "Points",
        id: "points",
        menuGroup: MenuGroups.DrivingLicence,
    },
    {
        title: "Age range",
        id: "ageRange",
        menuGroup: MenuGroups.DrivingLicence,
        hide: true,
    },
    {
        title: "Endorsement codes",
        id: "endorsementCodes",
        menuGroup: MenuGroups.DrivingLicence,
        hide: true,
    },
    {
        title: "Licence type",
        id: "licenceType",
        menuGroup: MenuGroups.DrivingLicence,
        hide: true,
    },
    {
        title: "Licence status",
        id: "licenceStatus",
        menuGroup: MenuGroups.DrivingLicence,
        hide: true,
    },
    {
        title: "Driver restrictions",
        id: "driverRestrictions",
        menuGroup: MenuGroups.DrivingLicence,
        hide: true,
    },
    {
        title: "All Licence Categories",
        id: "licenceCategories",
        menuGroup: MenuGroups.DrivingLicence,
        hide: true,
    },
    {
        title: "Category Expiry Dates",
        id: "categoryExpiryDates",
        menuGroup: MenuGroups.DrivingLicence,
        hide: true,
    },
    {
        title: "Expiring Categories",
        id: "expiringCategories",
        menuGroup: MenuGroups.DrivingLicence,
        hide: true,
    },
    {
        title: "Expired Categories",
        id: "expiredCategories",
        menuGroup: MenuGroups.DrivingLicence,
        hide: true,
    },

    {
        title: "Licence origin",
        id: "licenceOrigin",
        menuGroup: MenuGroups.DrivingLicence,
        hide: true,
    },
    {
        title: "Licence expires",
        id: "licenceExpires",
        menuGroup: MenuGroups.DrivingLicence,
        hide: true,
    },
    {
        title: "Years held",
        id: "yearsHeldPhotocard",
        menuGroup: MenuGroups.DrivingLicence,
        hide: true,
    },
    {
        title: "Licence number",
        id: "licenceNumber",
        hide: true,
        orderBy: DriverListOrderByOptions.LicenceNumber,
        menuGroup: MenuGroups.DrivingLicence,
    },
    {
        title: "Last Checked",
        id: "lastChecked",
        hide: true,
        orderBy: DriverListOrderByOptions.LastChecked,
        menuGroup: MenuGroups.DrivingLicence,
    },
    {
        title: "Previous Checks",
        id: "previousChecks",
        hide: true,
        menuGroup: MenuGroups.DrivingLicence,
    },
    {
        title: "Next Check",
        id: "nextCheck",
        hide: true,
        orderBy: DriverListOrderByOptions.NextCheck,
        menuGroup: MenuGroups.DrivingLicence,
    },
    {
        title: "CPC LGV Expiry Date",
        id: "cpcLgvExpiryDate",
        hide: true,
        menuGroup: MenuGroups.DrivingLicence,
    },
    {
        title: "CPC PCV Expiry Date",
        id: "cpcPcvExpiryDate",
        hide: true,
        menuGroup: MenuGroups.DrivingLicence,
    },
    {
        title: "Tacho Card Expiry Date",
        id: "tachoCardExpiryDate",
        hide: true,
        menuGroup: MenuGroups.DrivingLicence,
    },
    {
        title: "Linked driver status",
        id: "linkedDriverStatus",
        hide: true,
        menuGroup: MenuGroups.LinkedDriver,
    },
    {
        title: "Main driver name",
        id: "mainDriverName",
        hide: true,
        menuGroup: MenuGroups.LinkedDriver,
    },
    {
        title: "Comments",
        id: "comments",
        hide: true,
        menuGroup: MenuGroups.Comments,
    },
    {
        title: "Grey Fleet Invite Last Sent",
        id: "GreyFleetInviteEmailLastSent",
        hide: true,
        menuGroup: MenuGroups.VehicleInformation,
    },
    {
        title: "CPC Expiry Last Sent",
        id: "CpcExpiryEmailLastSent",
        hide: true,
        menuGroup: MenuGroups.DrivingLicence,
    },
    {
        title: "Non-address Match Last Sent",
        id: "AddressNonMatchEmailLastSent",
        hide: true,
        menuGroup: MenuGroups.Address,
    },
    {
        title: "Tacho Card Expiry Last Sent",
        id: "TachoCardExpiryEmailLastSent",
        hide: true,
        menuGroup: MenuGroups.DrivingLicence,
    },
    {
        title: "Photo Card Expiry Last Sent",
        id: "photoCardExpiryEmailLastSent",
        hide: true,
        menuGroup: MenuGroups.DrivingLicence,
    },
    {
        title: "Entitlement Expiry Last Sent",
        id: "entitlementExpiryEmailLastSent",
        hide: true,
        menuGroup: MenuGroups.DrivingLicence,
    },
    ...driverAddressTableHeaders,
    ...driverVehicleTableHeaders,
];

type DriverTableContents = TableContent<
    ApiDriverDetailsResponse,
    DriverTableAllowedIds
>;

export function initTableContents(
    drivers?: ApiDriverDetailsResponse[] | null,
    companyRiskProfiles?: CompanyRiskProfile[]
): DriverTableContents[] {
    const { getDateHighlightStyle } = useDueToExpireSettings();
    const tableContents = new Array<DriverTableContents>(0);
    drivers?.map((driver) => {
        const pointsColor = driverPointsColor(
            driver.totalPoints,
            companyRiskProfiles
        );

        const address: DriverAddressInformation = {
            postCode: driver.postCode,
            addressMatch: driver.addressMatch,
        };
        const vehicles: DriverVehicleInformation = {
            vehicleId: driver.vehicleId,
            vehicleRegistration: driver.vehicleRegistration,
            manufacturer: driver.manufacturer,
            euroRating: driver.euroRating,
            model: driver.model,
            yearOfManufacture: driver.yearOfManufacture,
            motExpiry: driver.motExpiry,
            engineCapacity: driver.engineCapacity,
            roadTaxExpiry: driver.roadTaxExpiry,
            co2Emissions: driver.co2Emissions,
            fuelType: driver.fuelType,
            insuranceType: driver.insuranceType,
            vehicleInsuranceUploaded: driver.vehicleInsuranceUploaded,
            vehicleInsuranceVerified: driver.vehicleInsuranceVerified,
            vehicleInsuranceExpiry: driver.vehicleInsuranceExpiry,
            businessCover: driver.businessCover,
        };

        tableContents.push({
            rowId: driver.driverId,
            rowData: driver,
            cellData: [
                {
                    id: "driverId",
                    content: <p>{driver.driverId}</p>,
                },
                {
                    id: "name",
                    content: (
                        <div className="flex items-center">
                            <UserIcon
                                firstName={driver.firstName}
                                lastName={driver.lastName}
                                size="small"
                            />
                            <p data-testid="driver-name">{`${driver.firstName} ${driver.lastName}`}</p>
                        </div>
                    ),
                },
                {
                    id: "emailAddress",
                    content: (
                        <p className="max-w-[15rem] truncate">
                            {driver.emailAddress}
                        </p>
                    ),
                },
                {
                    id: "dateOfBirth",
                    content: (
                        <p>
                            {driver.dateOfBirth
                                ? formatDateToString(
                                      new Date(driver.dateOfBirth)
                                  )
                                : ""}
                        </p>
                    ),
                },
                {
                    id: "deletedAt",
                    content: (
                        <p>
                            {driver.deletedAt
                                ? formatDateToString(new Date(driver.deletedAt))
                                : ""}
                        </p>
                    ),
                },
                {
                    id: "declarationStatus",
                    content: <p>{driver.declarationStatus}</p>,
                },
                {
                    id: "declarationLastSent",
                    content: (
                        <p>
                            {driver.declarationLastSent
                                ? formatDateToString(
                                      new Date(driver.declarationLastSent)
                                  )
                                : ""}
                        </p>
                    ),
                },
                {
                    id: "declarationExpires",
                    content: (
                        <p>
                            {driver.declarationExpiry
                                ? formatDateToString(
                                      new Date(driver.declarationExpiry)
                                  )
                                : ""}
                        </p>
                    ),
                },
                {
                    id: "client",
                    content: (
                        <p className="max-w-[15rem] truncate">
                            {driver.companyName}
                        </p>
                    ),
                },
                {
                    id: "department",
                    content: (
                        <p className="max-w-[15rem] truncate">
                            {driver.departmentName}
                        </p>
                    ),
                },
                {
                    id: "jobRole",
                    content: (
                        <p className="max-w-[15rem] truncate">
                            {driver.jobRole}
                        </p>
                    ),
                },
                {
                    id: "employeeNumber",
                    content: (
                        <p className="max-w-[15rem] truncate">
                            {driver.employeeNumber}
                        </p>
                    ),
                },
                {
                    id: "externalReference",
                    content: (
                        <p className="max-w-[15rem] truncate">
                            {driver.externalReference}
                        </p>
                    ),
                },
                {
                    id: "points",
                    content: (
                        <div className="pl-2">
                            <div
                                className={`h-7 w-7 rounded-full ${pointsColor} flex items-center justify-center`}
                            >
                                {driver.totalPoints ?? "-"}
                            </div>
                        </div>
                    ),
                },
                {
                    id: "ageRange",
                    content: <p>{driver.ageRange}</p>,
                },
                {
                    id: "endorsementCodes",
                    content: <p>{driver.endorsementCodes?.join(", ")}</p>,
                },
                {
                    id: "licenceType",
                    content: <p>{driver.licenceType}</p>,
                },
                {
                    id: "licenceStatus",
                    content: <p>{driver.licenceStatus}</p>,
                },
                {
                    id: "driverRestrictions",
                    content: <p>{driver.driverRestrictions?.join(", ")}</p>,
                },
                {
                    id: "licenceCategories",
                    content: <p>{driver.licenceCategories?.join(", ")}</p>,
                },
                {
                    id: "categoryExpiryDates",
                    content: (
                        <p>
                            {driver.categoryExpiryDates
                                ?.map((dateString, index) => {
                                    if (!dateString) return "";

                                    const categoryExpiryDatesLength =
                                        driver?.categoryExpiryDates?.length ??
                                        0;

                                    const showTrailingComma =
                                        index < categoryExpiryDatesLength - 1;

                                    const style = getDateHighlightStyle(
                                        dateString,
                                        "dueToExpireLicenceCategoryDays"
                                    );

                                    return (
                                        <HighlightedDate
                                            key={dateString}
                                            style={style}
                                            date={dateString}
                                            showTrailingComma={
                                                showTrailingComma
                                            }
                                        />
                                    );
                                })
                                .filter(Boolean)}
                        </p>
                    ),
                },
                {
                    id: "expiringCategories",
                    content: <p>{driver.expiringCategories?.join(", ")}</p>,
                },
                {
                    id: "expiredCategories",
                    content: <p>{driver.expiredCategories?.join(", ")}</p>,
                },
                {
                    id: "cpcLgvExpiryDate",
                    content: <p>
                                {driver.cpcLgvExpiryDate ? formatDateToString(
                                      new Date(driver.cpcLgvExpiryDate)
                                  ) : ""}
                            </p>,
                },
                {
                    id: "cpcPcvExpiryDate",
                    content: <p>
                                {driver.cpcPcvExpiryDate ? formatDateToString(
                                      new Date(driver.cpcPcvExpiryDate)
                                  ) : ""}
                            </p>,
                },
                {
                    id: "tachoCardExpiryDate",
                    content: <p>
                                {driver.tachoCardExpiryDate ? formatDateToString(
                                      new Date(driver.tachoCardExpiryDate)
                                  ) : ""}
                            </p>,
                },
                {
                    id: "licenceOrigin",
                    content: <p>{driver.licenceOrigin}</p>,
                },
                {
                    id: "licenceExpires",
                    content: (
                        <p>
                            {driver.licenceExpires
                                ? formatDateToString(
                                      new Date(driver.licenceExpires)
                                  )
                                : ""}
                        </p>
                    ),
                },
                {
                    id: "yearsHeldPhotocard",
                    content: <p>{driver.yearsHeldPhotocard}</p>,
                },
                {
                    id: "licenceNumber",
                    content: (
                        <p className="max-w-[15rem] truncate">
                            {driver.licenceNumber}
                        </p>
                    ),
                },
                {
                    id: "lastChecked",
                    content: (
                        <p className="max-w-[15rem] truncate">
                            {driver.licenceLastCheckedAtDate
                                ? formatDateToString(
                                      new Date(driver.licenceLastCheckedAtDate)
                                  )
                                : ""}
                        </p>
                    ),
                },
                {
                    id: "previousChecks",
                    content: (
                        <div className="flex flex-col gap-1">
                            <p className="max-w-[15rem] truncate">
                                {driver.previousLicenceCheckDateOne
                                    ? formatDateToString(
                                          new Date(
                                              driver.previousLicenceCheckDateOne
                                          )
                                      )
                                    : ""}
                            </p>
                            <p className="max-w-[15rem] truncate">
                                {driver.previousLicenceCheckDateTwo
                                    ? formatDateToString(
                                          new Date(
                                              driver.previousLicenceCheckDateTwo
                                          )
                                      )
                                    : ""}
                            </p>
                            <p className="max-w-[15rem] truncate">
                                {driver.previousLicenceCheckDateThree
                                    ? formatDateToString(
                                          new Date(
                                              driver.previousLicenceCheckDateThree
                                          )
                                      )
                                    : ""}
                            </p>
                        </div>
                    ),
                },
                {
                    id: "nextCheck",
                    content: (
                        <p className="max-w-[15rem] truncate">
                            {driver.licenceNextCheckDate
                                ? formatDateToString(
                                      new Date(driver.licenceNextCheckDate)
                                  )
                                : ""}
                        </p>
                    ),
                },
                {
                    id: "linkedDriverStatus",
                    content: <p>{driver.linkedDriverStatus}</p>,
                },
                {
                    id: "mainDriverName",
                    content: (
                        <p className="max-w-[15rem] truncate">
                            {driver.mainDriverName}
                        </p>
                    ),
                },
                {
                    id: "comments",
                    content: (
                        <div className="pl-6">
                            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-S2D-light-green-80">
                                {driver.comments}
                            </div>
                        </div>
                    ),
                },
                {
                    id: "GreyFleetInviteEmailLastSent",
                    content: (
                        <p>
                            {driver.greyFleetInviteLastSent
                                ? formatDateToString(
                                      new Date(driver.greyFleetInviteLastSent)
                                  )
                                : ""}
                        </p>
                    ),
                },
                {
                    id: "CpcExpiryEmailLastSent",
                    content: (
                        <p>
                            {driver.cpcExpiryLastSent
                                ? formatDateToString(
                                      new Date(driver.cpcExpiryLastSent)
                                  )
                                : ""}
                        </p>
                    ),
                },
                {
                    id: "AddressNonMatchEmailLastSent",
                    content: (
                        <p>
                            {driver.nonAddressMatchLastSent
                                ? formatDateToString(
                                      new Date(driver.nonAddressMatchLastSent)
                                  )
                                : ""}
                        </p>
                    ),
                },
                {
                    id: "TachoCardExpiryEmailLastSent",
                    content: (
                        <p>
                            {driver.tachoCardExpiryLastSent
                                ? formatDateToString(
                                      new Date(driver.tachoCardExpiryLastSent)
                                  )
                                : ""}
                        </p>
                    ),
                },
                {
                    id: "photoCardExpiryEmailLastSent",
                    content: (
                        <p>
                            {driver.photoCardExpiryEmailLastSent
                                ? formatDateToString(
                                      new Date(
                                          driver.photoCardExpiryEmailLastSent
                                      )
                                  )
                                : ""}
                        </p>
                    ),
                },
                {
                    id: "entitlementExpiryEmailLastSent",
                    content: (
                        <p>
                            {driver.entitlementExpiryEmailLastSent
                                ? formatDateToString(
                                      new Date(
                                          driver.entitlementExpiryEmailLastSent
                                      )
                                  )
                                : ""}
                        </p>
                    ),
                },
                ...driverAddressTableContent(address),
                ...driverVehicleTableContent(vehicles),
            ],
        });
    });
    return tableContents;
}
